import { ChangeEvent, useCallback, useState } from 'react'
import {
  PendingProcessHearingFilterType,
  PendingProcessHearingType,
} from 'types/pending-process-hearing.type'
import client from '../../../services/client'
import { BaseData, BaseDataContent } from 'types/base.type'
import { SystemMessages } from 'utils/enums/system-message.enum'
import { TeenagerProcessHearingType } from 'types/teenager-process-hearing.type'
import hearingService from '../../../services/hearing.service'
import { useSnackBar } from 'contexts/snackbar.context'
import { useBackdrop } from 'contexts/backdrop.context'
import { HttpErrorType } from 'types/http.type'
import { errorColor, errorMessage } from 'utils/error.util'

const ROWS_PER_PAGE_OPTIONS = [15, 24, 36, 45]
const DEFAULT_PAGE = 1
const ROWS_PER_PAGE = 15
const DEFAULT_ELEMENTS = 0
const FIRST_PAGE_NUMBER = 0

export const usePendingProcessHearing = () => {
  const [page, setPage] = useState(DEFAULT_PAGE)
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE)
  const [totalOfElements, setTotalOfElements] = useState(DEFAULT_ELEMENTS)
  const [totalOfPages, setTotalOfPages] = useState(DEFAULT_PAGE)
  const [loaded, setLoaded] = useState(false)

  const rowsOptions = ROWS_PER_PAGE_OPTIONS

  const { showSnackBar } = useSnackBar()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()

  const handleFirstPage = () => {
    setPage(FIRST_PAGE_NUMBER)
  }

  const handleNewPage = (e: unknown, newPage: number): void => {
    setPage(newPage - 1)
  }

  const handleRowsPerPage = (e: ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(+e.target.value)
    handleFirstPage()
  }

  const handleTotalOfElements = (totalOfElements: number): void =>
    setTotalOfElements(totalOfElements)

  const handleTotalOfPages = (totalOfPages: number): void => setTotalOfPages(totalOfPages)

  const url = '/integra-policial/processos'

  const [processos, setProcessos] = useState<PendingProcessHearingType[]>([])
  const [processo, setProcesso] = useState<PendingProcessHearingType>()
  const [totalProcesso, setTotalProcessos] = useState<number>(0)

  let currentController: AbortController | null = null // Variável de controle externa

  const buscarTodos = useCallback(
    async ({ tipo, ...filter }: PendingProcessHearingFilterType, resetPage = false) => {
      if (currentController) {
        // Evita chamadas simultâneas ou cancela a anterior
        currentController.abort()
      }

      const controller = new AbortController()
      currentController = controller

      try {
        const modifiedFilter = {
          ...filter,
          status: filter?.status.join(','),
        }

        if (resetPage) handleFirstPage()

        const newPage = resetPage ? FIRST_PAGE_NUMBER : page

        handleBackdropOpen()

        const urlEndpoint = tipo === 'NORMAL' ? url : `${url}-avancado`

        const response = await client.get<BaseDataContent<PendingProcessHearingType[]>>(
          urlEndpoint,
          {
            ...modifiedFilter,
            page: newPage,
            pageSize: rowsPerPage,
          },
        )

        if (response?.data?.content) {
          setProcessos(response.data.content)
          handleTotalOfElements(response.data.totalElements)
          setTotalProcessos(response.data.totalElements)
          handleTotalOfPages(response.data.totalPages)
        }
      } catch (error) {
        if (controller.signal.aborted) {
          console.log('Requisição abortada.')
        } else {
          console.error(error)
        }
      } finally {
        setLoaded(true)
        handleBackdropClose()
        if (currentController === controller) {
          currentController = null // Reseta o controlador
        }
      }
    },
    [page, rowsPerPage],
  )

  const buscarPorId = useCallback(async (id: string) => {
    handleBackdropOpen()
    await client
      .get<BaseData<PendingProcessHearingType>>(`${url}/${id}`)
      .then((response) => {
        if (response && response.data) {
          setProcesso(response.data)
        }
      })
      .catch((error: HttpErrorType) => showSnackBar(errorMessage(error), errorColor(error)))
      .finally(() => handleBackdropClose())
  }, [])

  const buscarPorNumeroCnj = useCallback(async (numeroCnj: string) => {
    handleBackdropOpen()
    await client
      .get<BaseData<PendingProcessHearingType>>(`${url}/numero-cnj/${numeroCnj}`)
      .then((response) => {
        if (response && response.data) {
          setProcesso(response.data)
        }
      })
      .catch((error: HttpErrorType) => showSnackBar(errorMessage(error), errorColor(error)))
      .finally(() => handleBackdropClose())
  }, [])

  const salvar = async (processId: string, props: PendingProcessHearingType) => {
    await client
      .put<BaseData<void>>(`${url}/${processId}`, props)
      .then(() => showSnackBar(SystemMessages.MESSAGE_001, 'success'))
      .catch((error: HttpErrorType) => showSnackBar(errorMessage(error), errorColor(error)))
  }

  const disableIniciarOitiva = (props?: TeenagerProcessHearingType) => {
    if (!props?.id) return true
    return !['PENDENTE', 'AGENDADO'].includes(props.status)
  }

  const disableInformarStatusOitiva = (props?: TeenagerProcessHearingType) => {
    if (!props?.id) return true
    return ['FINALIZADO'].includes(props.status)
  }

  const novaOitiva = async () => {
    const { idOitiva } = await hearingService.create().then((res) => res.data)

    return idOitiva
  }

  const resetProcessos = () => {
    setLoaded(false)
    setProcessos([])
  }

  return {
    processos,
    processo,
    totalProcesso,
    loaded,

    handleBuscarTodos: buscarTodos,
    handleBuscarPorId: buscarPorId,
    handleBuscarPorNumeroCnj: buscarPorNumeroCnj,
    handleSalvar: salvar,
    handleNovaOitiva: novaOitiva,
    disableIniciarOitiva,
    disableInformarStatusOitiva,
    resetProcessos,

    page,
    rowsPerPage,
    rowsOptions,
    totalOfPages,
    totalOfElements,
    handleRowsPerPage,
    handleNewPage,
  }
}
